import { useContext, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useAgent } from "@watermarkinsights/ai-toolbox";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { trapTabFocus } from "../../../../static/js/commons";
import {
  buildMessagePairs,
  deriveVisibleMessages,
  extractTextFromMessageContent,
} from "../ai-chat-message-utils";
import type { AgentMessage } from "../ai-chat-message-utils";
import DraftConversationPanel from "../draft-message/DraftConversationPanel";
import { useChatPanelState } from "./useChatPanelState";
import { usePanelResize } from "./usePanelResize";
import { useJumpToLatest } from "./useJumpToLatest";
import { ChatHeader } from "./ChatHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatFooter } from "./ChatFooter";
import { ClearConversationModal } from "./ClearConversationModal";

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  pendingMessage: string | null;
  onPendingMessageHandled: () => void;
}

export function ChatPanel({
  isOpen,
  onClose,
  pendingMessage,
  onPendingMessageHandled,
}: ChatPanelProps) {
  const intl = useIntl();
  const { sendMessage, isRunning, messages } = useAgent();
  const { setChatStreaming, isAiAssistantAvailable } = useContext<AppContextInterface>(AppContext);
  const isAiEnabled = isAiAssistantAvailable;
  const chatPanel = useChatPanelState();
  const {
    inputText,
    isAwaitingAgentResponse,
    panelWidthInPixels,
    isClearConversationModalOpen,
    clearedMessageCount,
    view,
    isDraftResultView,
    backToSelectionToken,
  } = chatPanel;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const { handleResizeStart } = usePanelResize(
    panelWidthInPixels,
    chatPanel.setPanelWidthInPixels,
  );

  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus();
    }
  }, [isOpen]);

  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    trapTabFocus(panelRef.current, e);
  };

  useEffect(() => {
    if (pendingMessage && !isAwaitingAgentResponse) {
      sendMessage(pendingMessage);
      chatPanel.setIsAwaitingAgentResponse(true);
      onPendingMessageHandled();
    }
  }, [pendingMessage]);

  const visibleMessages = deriveVisibleMessages(messages, clearedMessageCount);
  const isIntroState = visibleMessages.length === 0 && !isRunning;

  const latestUserMessageId =
    [...visibleMessages].reverse().find((m) => m.role === "user")?.id ?? null;

  const messagePairs = buildMessagePairs(visibleMessages);

  const lastVisibleMessage = visibleMessages[visibleMessages.length - 1];
  const lastAssistantMessage =
    lastVisibleMessage?.role === "assistant"
      ? (lastVisibleMessage as Extract<AgentMessage, { role: "assistant" }>)
      : null;
  const lastMessageIsAssistantWithText =
    lastAssistantMessage !== null &&
    (extractTextFromMessageContent(lastAssistantMessage.content).trim() !== "" ||
      (!isRunning && (lastAssistantMessage.toolCalls?.length ?? 0) > 0));

  useEffect(() => {
    if (isAwaitingAgentResponse && lastMessageIsAssistantWithText) {
      chatPanel.setIsAwaitingAgentResponse(false);
    }
  }, [isAwaitingAgentResponse, lastMessageIsAssistantWithText]);

  useEffect(() => {
    setChatStreaming(isAwaitingAgentResponse || isRunning);
  }, [isAwaitingAgentResponse, isRunning]);

  const {
    isJumpToLatestVisible,
    messagesContainerRef,
    latestExchangeAnchorRef,
    messagesEndRef,
    updateJumpToLatestVisibility,
    handleJumpToLatest,
  } = useJumpToLatest(visibleMessages, isRunning);

  const shouldShowTypingIndicator =
    isAwaitingAgentResponse && !lastMessageIsAssistantWithText;

  const handleSuggestedQuestionSelected = (question: string) => {
    if (isAwaitingAgentResponse || isRunning) return;
    sendMessage(question);
    chatPanel.setIsAwaitingAgentResponse(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInput = inputText.trim();
    if (!trimmedInput || isAwaitingAgentResponse) return;
    sendMessage(trimmedInput);
    chatPanel.setInputText("");
    chatPanel.setIsAwaitingAgentResponse(true);
    textareaRef.current?.focus();
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleFormSubmit(e as unknown as React.FormEvent);
    }
  };

  const handleConfirmClearConversation = () => {
    chatPanel.confirmClearConversation(messages.length);
  };

  return (
    <aside
      ref={panelRef}
      className={`tcw-panel${isOpen ? " is-open" : ""}`}
      aria-label={intl.formatMessage({ id: "app.toolboxChat.title" })}
      onKeyDown={handlePanelKeyDown}
    >
      <div className="tcw-resize-handle" onMouseDown={handleResizeStart} />

      <div
        className={`tcw-header${view === "draft" ? " tcw-header--draft" : ""}${
          isIntroState ? " tcw-header--no-border" : ""
        }`}
      >
        <ChatHeader
          view={view}
          isDraftResultView={isDraftResultView}
          onClose={onClose}
          onBackToConversation={chatPanel.closeDraftPanel}
          onBackToSelection={chatPanel.requestBackToSelection}
          onClearConversation={chatPanel.openClearConversationModal}
          onOpenDraftPanel={chatPanel.openDraftPanel}
          isAiEnabled={isAiEnabled}
        />
      </div>

      {view === "draft" ? (
        <div className="tcw-messages tcw-messages--draft">
          <DraftConversationPanel
            messagePairs={messagePairs}
            resetSignal={backToSelectionToken}
            onResultStateChange={chatPanel.setIsDraftResultView}
          />
        </div>
      ) : (
        <ChatMessageList
          isIntroState={isIntroState}
          visibleMessages={visibleMessages}
          latestUserMessageId={latestUserMessageId}
          isAwaitingAgentResponse={isAwaitingAgentResponse}
          isRunning={isRunning}
          shouldShowTypingIndicator={shouldShowTypingIndicator}
          isJumpToLatestVisible={isJumpToLatestVisible}
          messagesContainerRef={messagesContainerRef}
          latestExchangeAnchorRef={latestExchangeAnchorRef}
          messagesEndRef={messagesEndRef}
          onScroll={updateJumpToLatestVisibility}
          onQuestionSelected={handleSuggestedQuestionSelected}
          onJumpToLatest={handleJumpToLatest}
        />
      )}

      <ChatFooter
        view={view}
        inputText={inputText}
        isRunning={isRunning}
        textareaRef={textareaRef}
        onInputChange={chatPanel.setInputText}
        onSubmit={handleFormSubmit}
        onKeyDown={handleTextareaKeyDown}
      />

      <ClearConversationModal
        isOpen={isClearConversationModalOpen}
        onCancel={chatPanel.closeClearConversationModal}
        onConfirm={handleConfirmClearConversation}
      />
    </aside>
  );
}
